import { Plus, Trash2 } from 'lucide-react'
import type { LineItemDraft } from '@/shared/domain'
import { formatCurrency } from '@/shared/infra/format'
import { Button, Input, Label } from '@/shared/ui/primitives'
import { NumberDraftInput } from './number-draft-input'
import { NumberInput } from './number-input'

export type LineItemsEditorProps = {
  items: LineItemDraft[]
  total: number
  disabled?: boolean
  error?: string
  onAdd: () => void
  onRemove: (index: number) => void
  onUpdate: (index: number, patch: Partial<LineItemDraft>) => void
}

function parseNumberInput(raw: string): number | undefined {
  if (raw.trim() === '') return undefined
  const parsed = Number.parseFloat(raw)
  return Number.isNaN(parsed) ? undefined : parsed
}

function lineTotal(item: LineItemDraft): number {
  const quantity = item.quantity ?? 0
  const unitPrice = item.unitPrice ?? 0
  return Math.round((quantity * unitPrice + Number.EPSILON) * 100) / 100
}

export function LineItemsEditor({
  items,
  total,
  disabled,
  error,
  onAdd,
  onRemove,
  onUpdate,
}: LineItemsEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      <Label>Line items</Label>
      <div className="flex flex-col gap-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="flex flex-col gap-2 rounded-lg border bg-card p-3"
          >
            <Input
              aria-label={`Line item ${index + 1} description`}
              value={item.description}
              onChange={(e) => onUpdate(index, { description: e.target.value })}
              placeholder="Description"
              disabled={disabled}
            />
            <div className="flex items-end gap-2">
              <div className="flex flex-1 flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Qty</Label>
                <NumberInput
                  mode="integer"
                  aria-label={`Line item ${index + 1} quantity`}
                  value={item.quantity?.toString() ?? ''}
                  onChange={(next) =>
                    onUpdate(index, { quantity: parseNumberInput(next) })
                  }
                  placeholder="1"
                  disabled={disabled}
                />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Unit price</Label>
                <NumberDraftInput
                  mode="decimal"
                  aria-label={`Line item ${index + 1} unit price`}
                  value={item.unitPrice}
                  onChange={(v) => onUpdate(index, { unitPrice: v })}
                  placeholder="0.00"
                  disabled={disabled}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">Total</Label>
                <span className="flex h-9 items-center text-sm tabular-nums">
                  {formatCurrency(lineTotal(item))}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove line item ${index + 1}`}
                onClick={() => onRemove(index)}
                disabled={disabled || items.length <= 1}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
        >
          <Plus className="size-4" />
          Add line item
        </Button>
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-muted-foreground">Total</span>
          <span className="text-base font-semibold tabular-nums">
            {formatCurrency(total)}
          </span>
        </div>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
